import { useRef, useState } from 'react';
import { Hint, Panel, Slider } from './ui/Controls';
import { store, useStore } from '@/state/store';
import { readFileAsDataUrl } from '@/lib/utils';
import type { LogoCorner } from '@/types';

const CORNERS: { value: LogoCorner; label: string }[] = [
  { value: 'top-left', label: '↖' },
  { value: 'top-right', label: '↗' },
  { value: 'bottom-left', label: '↙' },
  { value: 'bottom-right', label: '↘' },
];

export function LogoUploader() {
  const logo = useStore((s) => s.logoDataUrl);
  const active = useStore((s) => s.variants.find((v) => v.id === s.activeId) ?? null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const logoSpec = active?.logo ?? null;

  const update = (patch: Partial<NonNullable<typeof logoSpec>>, key?: string) => {
    store.updateActive(
      (spec) => ({
        ...spec,
        logo: spec.logo ? { ...spec.logo, ...patch } : null,
      }),
      key,
    );
  };

  return (
    <Panel title="Logo">
      {logo ? (
        <div className="flex items-center gap-2.5">
          <div className="checkerboard flex h-12 w-12 shrink-0 items-center justify-center rounded-md p-1">
            <img src={logo} alt="Logo preview" className="max-h-full max-w-full object-contain" />
          </div>
          <button
            type="button"
            className="btn-ghost flex-1"
            onClick={() =>
              store.commit({
                logoDataUrl: null,
                variants: store
                  .getState()
                  .variants.map((v) => ({ ...v, logo: null })),
              })
            }
          >
            Remove logo
          </button>
        </div>
      ) : (
        <button type="button" className="btn-ghost w-full" onClick={() => inputRef.current?.click()}>
          Upload a logo
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/svg+xml,image/webp,image/jpeg"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          try {
            setError(null);
            const dataUrl = await readFileAsDataUrl(file);
            store.commit({ logoDataUrl: dataUrl });
          } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
          }
        }}
      />

      {error && <p className="mt-2 text-[10px] text-red-400">{error}</p>}

      {logo && logoSpec && (
        <div className="mt-3 space-y-2.5">
          <div>
            <span className="field-label">Corner</span>
            <div className="grid grid-cols-4 gap-1">
              {CORNERS.map((corner) => (
                <button
                  key={corner.value}
                  type="button"
                  title={corner.value.replace('-', ' ')}
                  onClick={() => update({ corner: corner.value, freePosition: false })}
                  className={`rounded-md py-1.5 text-sm transition ${
                    logoSpec.corner === corner.value && !logoSpec.freePosition
                      ? 'bg-accent text-ink-950'
                      : 'bg-white/[0.03] text-ink-300 hover:bg-white/[0.07]'
                  }`}
                >
                  {corner.label}
                </button>
              ))}
            </div>
            {logoSpec.freePosition && (
              <p className="mt-1 text-[10px] text-ink-500">
                Dragged to a custom position — pick a corner to snap back.
              </p>
            )}
          </div>

          <Slider
            label="Size"
            min={0.05}
            max={0.4}
            step={0.005}
            value={logoSpec.width}
            onChange={(v) => update({ width: v }, 'logo-width')}
            format={(v) => `${(v * 100).toFixed(0)}%`}
          />
          <Slider
            label="Opacity"
            min={0.1}
            max={1}
            value={logoSpec.opacity}
            onChange={(v) => update({ opacity: v }, 'logo-opacity')}
            format={(v) => `${(v * 100).toFixed(0)}%`}
          />
          <Slider
            label="Padding"
            min={0.01}
            max={0.14}
            step={0.002}
            value={logoSpec.padding}
            onChange={(v) => update({ padding: v }, 'logo-padding')}
            format={(v) => `${(v * 100).toFixed(1)}%`}
          />
        </div>
      )}

      {logo && !logoSpec && (
        <Hint>Generate designs to place the logo automatically.</Hint>
      )}
      {!logo && (
        <Hint>
          PNG with transparency works best. The engine picks the corner with the
          least overlap with the subject and the headline.
        </Hint>
      )}
    </Panel>
  );
}
